import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cake, ChevronRight, Sparkles, Heart, Activity } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Common veterinary horse-to-human age mapping (Equine Veterinary Journal / Stoneridge)
const ageTable = [
  { h: 1, hu: 6.5 }, { h: 2, hu: 13 }, { h: 3, hu: 18 }, { h: 4, hu: 20.5 },
  { h: 5, hu: 24.5 }, { h: 6, hu: 27 }, { h: 7, hu: 29.5 }, { h: 8, hu: 32 },
  { h: 9, hu: 34.5 }, { h: 10, hu: 36.5 },
];
const horseToHuman = (age) => {
  if (age <= 0) return 0;
  if (age <= 10) {
    const lo = ageTable.find(a => a.h === Math.floor(age));
    const hi = ageTable.find(a => a.h === Math.ceil(age));
    if (!lo || !hi) return 0;
    if (lo.h === hi.h) return lo.hu;
    return +(lo.hu + (hi.hu - lo.hu) * (age - lo.h)).toFixed(1);
  }
  return +(36.5 + (age - 10) * 2.5).toFixed(1);
};

const lifeStage = (age, isNl) => {
  if (age < 1) return isNl ? "Veulen" : "Foal";
  if (age < 2) return isNl ? "Jaarling" : "Yearling";
  if (age < 4) return isNl ? "Jong paard (in opleiding)" : "Young horse (in training)";
  if (age < 8) return isNl ? "Jongvolwassen sportpaard" : "Young adult sport horse";
  if (age < 16) return isNl ? "Volwassen / piek prestaties" : "Adult / peak performance";
  if (age < 22) return isNl ? "Veteraan" : "Veteran";
  return isNl ? "Senior" : "Senior";
};

const healthChecks = (age, isNl) => {
  if (age < 4) return isNl ? ["Ontwormen volgens fecaal-ei-telling", "Gebitscontrole jaarlijks", "Vaccinaties tetanus + influenza", "Hoefcontrole elke 6-8 wkn"] : ["Deworm based on fecal egg count", "Annual dental check", "Tetanus + influenza vaccinations", "Hoof trim every 6-8 weeks"];
  if (age < 16) return isNl ? ["Gebit 1-2x/jaar (rasper)", "Sportkeuringen voorafgaand aan seizoen", "Voer aanpassen aan trainingsniveau", "Bloedwaarden voor wedstrijdpaard"] : ["Dental 1-2x/year (rasping)", "Pre-season vet checks", "Adjust feed to training load", "Bloodwork for competition horses"];
  if (age < 22) return isNl ? ["Gebit 2x/jaar (verlies kiezen)", "Cushing-test (PPID) jaarlijks", "Gewricht- en peescontrole", "Senior-voer overwegen"] : ["Dental 2x/year (tooth loss possible)", "Annual Cushing's (PPID) test", "Joint & tendon screening", "Consider senior feed"];
  return isNl ? ["Dagelijks observatie + gewichtsmonitor", "Cushing/EMS-bloedonderzoek 2x/jaar", "Maatwerk voer (zacht, calorierijk)", "Gebit elke 6 mnd, vaak handvoeren"] : ["Daily observation + weight monitoring", "Cushing's/EMS bloodwork 2x/year", "Custom feed (soft, calorie-dense)", "Dental every 6 months, often hand-fed"];
};

const HorseAgeCalculatorPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const currentYear = new Date().getFullYear();
  const [birthYear, setBirthYear] = useState(currentYear - 8);
  const [birthMonth, setBirthMonth] = useState(4);
  const horseAge = useMemo(() => {
    const now = new Date();
    const months = (now.getFullYear() - birthYear) * 12 + (now.getMonth() + 1 - birthMonth);
    return Math.max(0, +(months / 12).toFixed(1));
  }, [birthYear, birthMonth]);
  const humanAge = useMemo(() => horseToHuman(horseAge), [horseAge]);

  useEffect(() => {
    document.title = isNl
      ? "Paardenleeftijd Calculator — bereken menselijke leeftijd van je paard | HorseAppCompare"
      : "Horse Age Calculator — convert horse years to human years | HorseAppCompare";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", isNl
      ? "Hoe oud is je paard in menselijke jaren? Bereken het direct met onze veterinaire formule. Inclusief levensfase en welke gezondheidschecks bij die leeftijd horen."
      : "How old is your horse in human years? Calculate instantly with our veterinary formula. Includes life stage and recommended health checks for that age.");
  }, [isNl]);

  return (
    <main data-testid="horseage-page">
      <section className="bg-gradient-to-br from-indigo-50 via-white to-white border-b border-[#E2E8F0]">
        <div className="hac-container py-14 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-4"><Link to="/" className="hover:text-[#2563EB]">Home</Link><ChevronRight size={12} /><span className="text-[#0F172A] font-medium">{isNl ? "Paardenleeftijd" : "Horse age"}</span></nav>
          <div className="overline mb-2 text-indigo-600 inline-flex items-center gap-1.5"><Cake size={12} /> {isNl ? "Gratis tool" : "Free tool"}</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-3">{isNl ? "Paardenleeftijd Calculator" : "Horse Age Calculator"}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">{isNl
            ? "Reken de leeftijd van je paard om naar menselijke jaren. Begrijp in welke levensfase hij zit en welke gezondheidschecks daarbij horen."
            : "Convert your horse's age to human years. Understand which life stage they're in and which health checks belong to that age."}</p>
        </div>
      </section>

      <section className="hac-container py-10 max-w-6xl grid lg:grid-cols-2 gap-6">
        <form onSubmit={(e) => e.preventDefault()} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-5">
          <h2 className="font-bold text-xl text-[#0F172A]">{isNl ? "Geboortedatum van je paard" : "Your horse's birth date"}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Geboortejaar" : "Birth year"}</label><input type="number" min={currentYear - 40} max={currentYear} className="hac-input" value={birthYear} onChange={(e) => setBirthYear(parseInt(e.target.value || `${currentYear}`))} data-testid="age-year" /></div>
            <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Geboortemaand" : "Birth month"}</label><select className="hac-input" value={birthMonth} onChange={(e) => setBirthMonth(parseInt(e.target.value))} data-testid="age-month">
              {Array.from({length: 12}, (_, i) => i + 1).map(m => <option key={m} value={m}>{new Date(2020, m - 1, 1).toLocaleDateString(isNl ? "nl-NL" : "en-GB", { month: "long" })}</option>)}
            </select></div>
          </div>
          <p className="text-[11px] text-[#94A3B8]">{isNl ? "Tip: officieel rekenen alle paarden vanaf 1 januari één jaar bij. Wij rekenen vanaf werkelijke geboortedatum." : "Tip: officially every horse gains a year on 1 January. We calculate from the real birth date."}</p>
        </form>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest text-indigo-200 mb-2 inline-flex items-center gap-1.5"><Sparkles size={12} /> {isNl ? "Menselijke leeftijd" : "Human age equivalent"}</div>
            <div className="text-6xl font-bold mb-1 tabular-nums" data-testid="age-result">{humanAge} <span className="text-2xl text-indigo-200">{isNl ? "jaar" : "yrs"}</span></div>
            <div className="text-indigo-200 text-sm">{horseAge} {isNl ? "paardenjaren" : "horse years"} · {lifeStage(horseAge, isNl)}</div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <h3 className="font-bold text-[#0F172A] mb-3 inline-flex items-center gap-2"><Heart size={16} className="text-rose-500" /> {isNl ? "Aanbevolen gezondheidschecks" : "Recommended health checks"}</h3>
            <ul className="space-y-2 text-sm text-[#0F172A]">
              {healthChecks(horseAge, isNl).map((h, i) => <li key={i} className="flex gap-2"><span className="text-indigo-500">●</span>{h}</li>)}
            </ul>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <h3 className="font-bold text-[#0F172A] mb-3 inline-flex items-center gap-2"><Activity size={16} className="text-indigo-600" /> {isNl ? "Vergelijking met andere leeftijden" : "Compare with other ages"}</h3>
            <div className="grid grid-cols-4 gap-2 text-xs">
              {[1, 5, 10, 15, 20, 25, 30, 35].map((h) => (
                <div key={h} className={`p-2 rounded text-center ${Math.round(horseAge) === h ? "bg-indigo-600 text-white" : "bg-slate-50"}`}>
                  <div className="font-mono font-bold">{h}{isNl ? "jr" : "y"}</div>
                  <div className="text-[10px] text-[#64748B]">≈ {horseToHuman(h)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HorseAgeCalculatorPage;
